import { createRouter, createWebHistory } from 'vue-router';
import LoginPage from '../views/auth/login/LoginPage.vue';
import ForgotPasswordPage from "@/views/auth/restorePassword/ForgotPasswordPage.vue";
import ResetPasswordPage from "@/views/auth/restorePassword/ResetPasswordPage.vue";
import RegisterPage from "@/views/auth/registration/RegistrationFirstStepPage.vue";
import PrivacyPolicyPage from "@/views/PrivacyPolicyPage.vue";
import RegistrationForm from "@/views/auth/registration/RegistrationLastStepPage.vue";
import ProfilePage from "@/views/profile/ProfilePage.vue";
import AuthCallbackPage from "@/views/AuthCallback.vue";
import store from "@/store"
import RegistrationByEmailPage from "@/views/auth/registration/RegistrationByEmailPage.vue";
import PaymentLinkPage from "@/views/payment/PaymentLinkPage.vue";
import WordPressContentPage from "@/views/content/WordPressContentPage.vue";
import ChatPage from "@/views/chat/ChatPage.vue";
import EmbedChatPage from "@/views/chat/EmbedChatPage.vue";
import ChatPublicReportPage from "@/views/chat/ChatPublicReportPage.vue";
import ChatPrintPage from "@/views/chat/ChatPrintPage.vue";
import ChatSharePage from "@/views/chat/ChatSharePage.vue";
import { trackPageView } from "@/utils/analytics";

/*
 * Админская часть приложения вынесена в отдельный webpack-чанк (`admin.<hash>.js`).
 * Обычные пользователи никогда не загружают этот код — он подтягивается лениво только
 * после успешной проверки роли в `requireAdmin` ниже. То же касается админских утилит
 * (`adminChatApi.js`, `AdminChatPlanForm.vue`) — они импортируются только из этих
 * страниц, поэтому webpack автоматически уезжают в тот же admin-чанк.
 */
const AdminLayout = () => import(/* webpackChunkName: "admin" */ "@/views/admin/AdminLayout.vue");
const AdminDashboardPage = () => import(/* webpackChunkName: "admin" */ "@/views/admin/AdminDashboardPage.vue");
const AdminDialogsPage = () => import(/* webpackChunkName: "admin" */ "@/views/admin/AdminDialogsPage.vue");
const AdminThreadPage = () => import(/* webpackChunkName: "admin" */ "@/views/admin/AdminThreadPage.vue");
const AdminUsagePage = () => import(/* webpackChunkName: "admin" */ "@/views/admin/AdminUsagePage.vue");
const AdminAbusePage = () => import(/* webpackChunkName: "admin" */ "@/views/admin/AdminAbusePage.vue");
const AdminLogsPage = () => import(/* webpackChunkName: "admin" */ "@/views/admin/AdminLogsPage.vue");
const AdminChatPlansPage = () => import(/* webpackChunkName: "admin" */ "@/views/admin/AdminChatPlansPage.vue");
const AdminChatSourcesPage = () => import(/* webpackChunkName: "admin" */ "@/views/admin/AdminChatSourcesPage.vue");
const AdminReportsPage = () => import(/* webpackChunkName: "admin" */ "@/views/admin/AdminReportsPage.vue");
const AdminChatSourcePage = () => import(/* webpackChunkName: "admin" */ "@/views/admin/AdminChatSourcePage.vue");
const AdminMarketingProfilesPage = () => import(/* webpackChunkName: "admin" */ "@/views/admin/AdminMarketingProfilesPage.vue");
const AdminSegmentsPage = () => import(/* webpackChunkName: "admin" */ "@/views/admin/AdminSegmentsPage.vue");
const AdminAiErrorsPage = () => import(/* webpackChunkName: "admin" */ "@/views/admin/AdminAiErrorsPage.vue");
const AdminUsersPage = () => import(/* webpackChunkName: "admin" */ "@/views/admin/AdminUsersPage.vue");
const AdminUserPage = () => import(/* webpackChunkName: "admin" */ "@/views/admin/AdminUserPage.vue");
const AdminPaymentsPage = () => import(/* webpackChunkName: "admin" */ "@/views/admin/AdminPaymentsPage.vue");
const AdminTicketsPage = () => import(/* webpackChunkName: "admin" */ "@/views/admin/AdminTicketsPage.vue");
const AdminPublicReportsPage = () => import(/* webpackChunkName: "admin" */ "@/views/admin/AdminPublicReportsPage.vue");
const AdminPromptSuggestionsPage = () => import(/* webpackChunkName: "admin" */ "@/views/admin/AdminPromptSuggestionsPage.vue");

const ROLE_ADMIN = 2;

/**
 * Асинхронный guard для админской ветки.
 * Не полагается на устаревший userInfo из localStorage: дёргает /api/users/{id},
 * получает свежий role_id и пускает только если бэк подтвердил админство.
 * Если бэк сказал «не админ» — редирект на /chat (точно так же, как делает middleware AdminOnly).
 */
async function requireAdmin(to, from, next) {
    if (!localStorage.getItem('token')) {
        return next({ path: '/login', query: { redirect: to.fullPath } });
    }
    const fresh = await store.dispatch('main/refreshCurrentUser');
    const roleId = Number(fresh?.role_id);
    if (roleId !== ROLE_ADMIN) {
        return next('/chat');
    }
    return next();
}

const routes = [
    {
        path: '/',
        name: 'Home',
        component: ChatPage
    },
    {
        path: '/chat',
        name: 'Chat',
        component: ChatPage
    },
    {
        path: '/embed',
        name: 'Embed',
        component: EmbedChatPage
    },
    {
        path: '/chat/report/:token',
        name: 'PublicReport',
        component: ChatPublicReportPage
    },
    {
        // Печатный экспорт диалога (открывается в новой вкладке из ChatExportModal).
        // Только для авторизованных: PDF сохраняется в раздел «Мои файлы»
        // текущего пользователя. Guest пользователи направляются на /login
        // самим ChatExportModal, здесь дублируем защитой на уровне маршрута —
        // чтобы прямой ход в /chat/print не открывал функциональность.
        path: '/chat/print',
        name: 'ChatPrint',
        component: ChatPrintPage,
        meta: { auth: true }
    },
    {
        path: '/chat/share/:token',
        name: 'ChatShare',
        component: ChatSharePage
    },
    {
        path: '/login',
        name: 'Login',
        component: LoginPage,
        meta: { unauthorizedOnly: true }
    },
    {
        path: '/register-first-step',
        name: 'Register',
        component: RegisterPage,
        meta: { unauthorizedOnly: true }
    },
    {
        path: '/register-confirm',
        name: 'RegistrationByEmail',
        component: RegistrationByEmailPage,
        meta: { unauthorizedOnly: true }
    },
    {
        path: '/forgot-password',
        name: 'Forgot password',
        component: ForgotPasswordPage,
        meta: { unauthorizedOnly: true }
    },
    {
        path: '/reset-password',
        name: 'Reset password',
        component: ResetPasswordPage
    },
    {
        path: '/privacy-policy',
        name: 'Privacy policy',
        component: PrivacyPolicyPage
    },
    {
        path: '/register',
        name: 'AccountCreation',
        component: RegistrationForm,
        meta: { requiresVerification: true }
    },
    {
        path: '/analytics',
        redirect: { name: 'Reports' },
    },
    {
        path: '/reports',
        name: 'Reports',
        component: ChatPage,
        meta: { auth: true }
    },
    {
        path: '/profile',
        name: 'Profile',
        component: ProfilePage,
        meta: { auth: true }
    },
    {
        path: '/content/:slug?',
        name: 'Content',
        component: WordPressContentPage,
        meta: { auth: true }
    },
    {
        path: '/pay/:token',
        name: 'PaymentLink',
        component: PaymentLinkPage
    },
    {
        path: '/auth/callback',
        name: 'AuthCallback',
        component: AuthCallbackPage,
        meta: { unauthorizedOnly: true }
    },
    {
        path: '/admin/chat',
        component: AdminLayout,
        meta: { auth: true },
        beforeEnter: requireAdmin,
        children: [
            { path: '', name: 'AdminDashboard', component: AdminDashboardPage },
            { path: 'users', name: 'AdminUsers', component: AdminUsersPage },
            { path: 'users/:id', name: 'AdminUser', component: AdminUserPage },
            { path: 'dialogs', name: 'AdminDialogs', component: AdminDialogsPage },
            { path: 'dialogs/:externalId', name: 'AdminThread', component: AdminThreadPage },
            { path: 'marketing', name: 'AdminMarketing', component: AdminMarketingProfilesPage },
            { path: 'segments', name: 'AdminSegments', component: AdminSegmentsPage },
            { path: 'ai-errors', name: 'AdminAiErrors', component: AdminAiErrorsPage },
            { path: 'usage', name: 'AdminUsage', component: AdminUsagePage },
            { path: 'abuse', name: 'AdminAbuse', component: AdminAbusePage },
            { path: 'logs', name: 'AdminLogs', component: AdminLogsPage },
            { path: 'plans', name: 'AdminChatPlans', component: AdminChatPlansPage },
            { path: 'sources', name: 'AdminChatSources', component: AdminChatSourcesPage },
            // Отдельная страница управления одним источником: редактирование
            // карточки + файлы (пагинация, поиск, фильтр статусов). Для RAG
            // нужно удобно работать с десятками тысяч файлов.
            {
                path: 'sources/:id(\\d+)',
                name: 'AdminChatSource',
                component: AdminChatSourcePage,
                props: true,
            },
            { path: 'payments', name: 'AdminPayments', component: AdminPaymentsPage },
            { path: 'tickets', name: 'AdminTickets', component: AdminTicketsPage },
            { path: 'public-reports', name: 'AdminPublicReports', component: AdminPublicReportsPage },
            { path: 'reports', name: 'AdminReports', component: AdminReportsPage },
            { path: 'prompt-suggestions', name: 'AdminPromptSuggestions', component: AdminPromptSuggestionsPage },
        ]
    }
];

const index = createRouter({
    history: createWebHistory(),
    routes,
});

// SPA-hit для Яндекс.Метрики (счётчик 110407517 работает с defer:true).
// Шлём просмотр на КАЖДУЮ навигацию, включая первую загрузку страницы —
// иначе визиты в этом счётчике не создаются и ecommerce/цели не привязываются.
index.afterEach((to, from) => {
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    const url = origin + to.fullPath;
    const referrer = from && from.fullPath && from.fullPath !== to.fullPath
        ? origin + from.fullPath
        : (typeof document !== 'undefined' ? document.referrer : '');
    trackPageView(url, referrer);
});

index.beforeEach((to, from, next) => {
    const userInfo = store.getters['main/userInfo'];
    const isAuthenticated = Object.keys(userInfo).length > 0;

    if (to.meta.auth && !isAuthenticated) {
        return next({
            path: '/login',
            query: { redirect: to.fullPath }
        });
    }

    if (to.meta.unauthorizedOnly && isAuthenticated) {
        return next('/chat');
    }

    if (to.path === '/login' && isAuthenticated) {
        return next('/chat');
    }

    if (to.meta.requiresVerification) {
        return localStorage.getItem('isVerified') !== 'true' ? next('/') : next();
    }

    return next();
});


export default index;
